import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProcessTemplateTaskType } from '@econtractor/types';
import TableEmptyRow from 'components/others/TableEmptyRow';
import { classifyProcessDiagram } from '../../processDiagram/helpers/diagramProgress';
import { processTaskInstanceKey } from '../../processDiagram/helpers/nextTask';
import ProcessTasksTableRow from './ProcessTasksTableRow';
import type { ProcessTaskDuePatch } from './ProcessTaskDueDialog';

interface ProcessTasksTableBodyProps {
    tasks: ProcessTemplateTaskType[];
    disabled?: boolean;
    launched?: boolean;
    onEdit: (task: ProcessTemplateTaskType) => void;
    onRemove: (task: ProcessTemplateTaskType) => void;
    onDueChange?: (dtId: number, patch: ProcessTaskDuePatch) => void;
    onOpenInstance?: (instanceId: number) => void;
}

const ProcessTasksTableBody = ({
    tasks,
    disabled,
    launched = false,
    onEdit,
    onRemove,
    onDueChange,
    onOpenInstance,
}: ProcessTasksTableBodyProps) => {
    const { t } = useTranslation(['Tasks']);
    const prunedKeys = useMemo(() => {
        if (!launched) return new Set<string>();
        return classifyProcessDiagram(tasks).prunedKeys;
    }, [launched, tasks]);

    if (!tasks.length) {
        return (
            <tbody>
                <TableEmptyRow colSpan={5}>{t('processDir.emptyTasks')}</TableEmptyRow>
            </tbody>
        );
    }

    return (
        <tbody>
            {tasks.map((task) => (
                <ProcessTasksTableRow
                    key={task.dtId}
                    task={task}
                    tasks={tasks}
                    disabled={disabled}
                    launched={launched}
                    pruned={prunedKeys.has(processTaskInstanceKey(task.templateId, task.dtId))}
                    onEdit={onEdit}
                    onRemove={onRemove}
                    onDueChange={onDueChange}
                    onOpenInstance={onOpenInstance}
                />
            ))}
        </tbody>
    );
};

export default ProcessTasksTableBody;
